import React from 'react'
import { HelpCircle, PlayCircle, Pencil, RotateCcw, Calendar, BarChart3, Info, Sparkles } from 'lucide-react'
import ClientDashboardV3ScoreGauge from '../ClientDashboardV3ScoreGauge'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import ReportService from '../../../../services/reports/Report.service'
import ClientDataService from '../../../../services/ClientData.service'
import CurrentClientProvider from '../../../../providers/CurrentClientProvider'
import EpochMonthService from '../../../../services/EpochMonth.service'
import {
  VERTICAL_BENCHMARKS,
  FRESHNESS_WINDOW_DAYS,
  FRESHNESS_CAP_PCT,
  BAR_BENCHMARK_POS,
  metricBarPosition,
  percentileFromQuantiles,
  resolveClientVertical,
  computeHealthScore,
  type ScoringBenchmarks,
  healthMetricDisplayLabel,
  metricScoreTone,
  type RetailVertical,
  type HealthScoreInputs,
} from '../../../../constants/healthScoreBenchmarks'
import { computeMetricInsight } from '../../../../constants/healthInsightsLogic'
import { HealthScoreDrawer } from './HealthScoreDrawer'
import { ScoreHistorySection, type MonthlyScorePoint, type HistoryMetricKey } from './ScoreHistorySection'
import type { DashboardFilterRequestParams } from '../../../../services/dashboards/dashboardFilters'
import { VideoPlayerModal } from './VideoPlayerModal'
import { cn } from '../../../../lib/utils'
import KpiHelpIcon, { type KpiTooltipKey } from './KpiHelpIcon'
import { requestOrbitIndiePanelOpen } from '../../../../services/orbit/orbitPageAssistantBridge'

// SPS-5570: benchmarks are the top quartile (P75) of the vertical. Decided value is "top 25%".
export const BENCHMARK_TOP_PERCENT_LABEL = 'top 25%'

// Percentiles are 0–100; clamp to an integer, or null when missing / non-finite so the chip hides.
export function normalizePercentile(raw: number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null
  const num = Number(raw)
  if (!Number.isFinite(num)) return null
  return Math.min(100, Math.max(0, Math.round(num)))
}

export function formatOrdinal(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

export interface HealthCashFlowApiData {
  salesTY: number;
  markdownsTY: number;
  cashMarginTY: number;
  inflowCostTY: number;
  inflowRetailTY: number;
  grossMarginTY: number;
  salesTC: number;
  markdownsTC: number;
  cashMarginTC: number;
  inflowCostTC: number;
  inflowRetailTC: number;
  grossMarginTC: number;
  invAvgCostThisYear: number;
  invAvgCostComp: number;
  invAvgRetailThisYear: number;
  invAvgRetailComp: number;
  inventoryCurrentThisYear: number;
  inventoryCurrentComp: number;
  // Receipts at retail over the last 2 closed months (freshness window) and a year earlier.
  inflowRetail60TY?: number;
  inflowRetail60TC?: number;
}

// Freshness = 60-day receipts ÷ current inventory, capped at FRESHNESS_CAP_PCT; null (not
// scored) when there is no current inventory.
function freshnessPct(inflowRetail60: number, inventoryCurrent: number): number | null {
  if (!inventoryCurrent) return null
  return Math.min(FRESHNESS_CAP_PCT, (inflowRetail60 / inventoryCurrent) * 100)
}

export interface HealthCashFlowMetrics {
  sales: number;
  salesComp: number;
  markdowns: number;
  markdownsComp: number;
  markdownPct: number;
  markdownPctComp: number;
  cashMargin: number;
  cashMarginComp: number;
  cashMarginPct: number;
  cashMarginPctComp: number;
  inflowCost: number;
  inflowCostComp: number;
  costOfPurchasesPct: number;
  costOfPurchasesPctComp: number;
  grossMargin: number;
  grossMarginComp: number;
  grossMarginPct: number;
  grossMarginPctComp: number;
  gmroi: number;
  gmroiComp: number;
  cmroi: number;
  cmroiComp: number;
  invAvgCost: number;
  invAvgCostComp: number;
  turn: number;
  turnComp: number;
  freshness: number | null;
  freshnessComp: number | null;
  imuPct: number;
  imuPctComp: number;
}

export function deriveHealthCashFlowMetrics(data: HealthCashFlowApiData | null): HealthCashFlowMetrics | null {
  if (!data) return null

  const sales = data.salesTY || 0
  const salesComp = data.salesTC || 0
  const markdowns = data.markdownsTY || 0
  const markdownsComp = data.markdownsTC || 0
  const cashMargin = data.cashMarginTY || 0
  const cashMarginComp = data.cashMarginTC || 0
  const inflowCost = data.inflowCostTY || 0
  const inflowCostComp = data.inflowCostTC || 0
  const inflowRetail = data.inflowRetailTY || 0
  const inflowRetailComp = data.inflowRetailTC || 0
  const grossMargin = data.grossMarginTY || 0
  const grossMarginComp = data.grossMarginTC || 0
  const invAvgCost = data.invAvgCostThisYear || 0
  const invAvgCostComp = data.invAvgCostComp || 0
  const invAvgRetail = data.invAvgRetailThisYear || 0
  const invAvgRetailComp = data.invAvgRetailComp || 0
  const inventoryCurrent = data.inventoryCurrentThisYear || 0
  const inventoryCurrentComp = data.inventoryCurrentComp || 0

  return {
    sales,
    salesComp,
    markdowns,
    markdownsComp,
    markdownPct: sales ? markdowns / sales : 0,
    markdownPctComp: salesComp ? markdownsComp / salesComp : 0,
    cashMargin,
    cashMarginComp,
    cashMarginPct: sales ? (cashMargin / sales) * 100 : 0,
    cashMarginPctComp: salesComp ? (cashMarginComp / salesComp) * 100 : 0,
    inflowCost,
    inflowCostComp,
    costOfPurchasesPct: ReportService.calculateCop({ inflowCost, sales }) * 100,
    costOfPurchasesPctComp: ReportService.calculateCop({ inflowCost: inflowCostComp, sales: salesComp }) * 100,
    grossMargin,
    grossMarginComp,
    grossMarginPct: sales ? (grossMargin / sales) * 100 : 0,
    grossMarginPctComp: salesComp ? (grossMarginComp / salesComp) * 100 : 0,
    gmroi: invAvgCost ? grossMargin / invAvgCost : 0,
    gmroiComp: invAvgCostComp ? grossMarginComp / invAvgCostComp : 0,
    cmroi: invAvgCost ? cashMargin / invAvgCost : 0,
    cmroiComp: invAvgCostComp ? cashMarginComp / invAvgCostComp : 0,
    invAvgCost,
    invAvgCostComp,
    turn: ReportService.calculateTurn({ sales, invAvg: invAvgRetail, format: false }) as number,
    turnComp: ReportService.calculateTurn({ sales: salesComp, invAvg: invAvgRetailComp, format: false }) as number,
    freshness: freshnessPct(data.inflowRetail60TY || 0, inventoryCurrent),
    freshnessComp: freshnessPct(data.inflowRetail60TC || 0, inventoryCurrentComp),
    // IMU (initial markup) on purchases = (received retail − received cost) / received retail.
    // This is the client's own figure; the modal shows it as a non-scored "second margin driver".
    imuPct: inflowRetail ? ((inflowRetail - inflowCost) / inflowRetail) * 100 : 0,
    imuPctComp: inflowRetailComp ? ((inflowRetailComp - inflowCostComp) / inflowRetailComp) * 100 : 0,
  }
}

export type ScoreTone = 'excellent' | 'good' | 'average' | 'poor' | 'fair'

export type HealthMetricKey = 'salesGrowth' | 'turn' | 'markdowns' | 'freshness' | 'grossMargin' | 'cashMargin'

export interface HealthSnapshotRow {
  label: string;
  badge: string;
  tone: ScoreTone;
  filled: number;
  caption: string;
  metricKey: HealthMetricKey;
}

export interface HealthSnapshotSummary {
  score: number;
  compareLabel: string;
  rows: HealthSnapshotRow[];
}

export interface CashFlowSummaryRow {
  label: string;
  dotColor: string;
  value: string;
  sub: string;
  delta?: string;
  positive?: boolean;
  strong?: boolean;
}

export interface RoiSummaryCard {
  label: string;
  value: string;
  helper: string;
  delta: string;
  positive: boolean;
}

const scoreBadgeStyles: Record<ScoreTone, { bg: string; text: string }> = {
  excellent: { bg: '#17B26A', text: '#FFFFFF' },
  good: { bg: '#ECFDF3', text: '#027A48' },
  average: { bg: '#FEFBE8', text: '#A16207' },
  fair: { bg: '#FEF6EE', text: '#B54708' },
  poor: { bg: '#FEF3F2', text: '#B42318' },
}

// SPS-5569 "Health Score Interactive" driver tone pill — a LIGHT tinted pill with a colored
// dot (NOT the solid-green Excellent pill). Mirrors the II card's `toneLight` map exactly.
const TONE_LIGHT: Record<ScoreTone, { dot: string; fg: string; bg: string }> = {
  excellent: { dot: '#17B26A', fg: '#027A48', bg: '#ECFDF3' },
  good: { dot: '#85CB33', fg: '#3B6D11', bg: '#F3FAE8' },
  average: { dot: '#EAAA08', fg: '#A16207', bg: '#FEFBE8' },
  fair: { dot: '#F79009', fg: '#B54708', bg: '#FEF6EE' },
  poor: { dot: '#F04438', fg: '#B42318', bg: '#FEF3F2' },
}

const EXCELLENT_BAR_COLORS = ['#C0DD97', '#97C459', '#639922', '#3B6D11', '#27500A']
const TONE_BAR_COLORS: Record<ScoreTone, string> = {
  excellent: '#16A34A',
  good: '#65A30D',
  average: '#D97706',
  fair: '#EA580C',
  poor: '#DC2626',
}

function getSegmentColor(tone: ScoreTone, index: number, filled: number, lowerIsBetter = false): string {
  if (index >= filled) return '#E5E7EB'
  if (tone === 'excellent' || tone === 'good') {
    const palette = lowerIsBetter ? [...EXCELLENT_BAR_COLORS].reverse() : EXCELLENT_BAR_COLORS
    return palette[index] ?? '#16A34A'
  }
  return TONE_BAR_COLORS[tone] ?? '#E5E7EB'
}

function HelpDot({ tooltipKey }: { tooltipKey?: KpiTooltipKey }) {
  if (!tooltipKey) {
    return <HelpCircle className="h-3 w-3 text-gray-400" />
  }

  return <KpiHelpIcon tooltipKey={tooltipKey} />
}

function resolveHealthRowTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Gross Margin':
      return 'grossMargin'
    case 'Markdowns':
      return 'markdowns'
    case 'Annual Turn':
      return 'annualTurn'
    case 'Sales Growth':
      return 'salesGrowth'
    case 'Inventory Freshness':
      return 'inventoryFreshness'
    default:
      return null
  }
}

function resolveCashFlowTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Cash Margin':
      return 'cashMargin'
    case 'Gross Margin':
      return 'grossMargin'
    case 'Markdowns':
      return 'markdowns'
    default:
      return null
  }
}

function DeltaBadge({ delta, invert = false }: { delta: number; invert?: boolean }) {
  const positive = delta >= 0
  const good = invert ? !positive : positive
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-semibold', good ? 'text-emerald-600' : 'text-red-600')}>
      <span className="text-[10px] leading-none">{positive ? '↗' : '↘'}</span>
      {positive ? '+' : ''}{delta.toFixed(1)}
    </span>
  )
}

function formatSignedPercent(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatPercent(value)}`
}

function formatSignedPointDelta(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatRatio(value)} pts`
}

function formatSignedRatio(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatRatio(value)}`
}

function percentageDelta(current: number, compare: number) {
  if (!compare) return 0
  return ((current - compare) / Math.abs(compare)) * 100
}

function toScoreTone(labelValue: number, thresholds: { excellent: number; good: number; average: number; fair: number }, lowerIsBetter = false): ScoreTone {
  if (lowerIsBetter) {
    if (labelValue <= thresholds.excellent) return 'excellent'
    if (labelValue <= thresholds.good) return 'good'
    if (labelValue <= thresholds.average) return 'average'
    if (labelValue <= thresholds.fair) return 'fair'
    return 'poor'
  }

  if (labelValue >= thresholds.excellent) return 'excellent'
  if (labelValue >= thresholds.good) return 'good'
  if (labelValue >= thresholds.average) return 'average'
  if (labelValue >= thresholds.fair) return 'fair'
  return 'poor'
}

function badgeForTone(tone: ScoreTone) {
  switch (tone) {
    case 'excellent': return 'Excellent'
    case 'good': return 'Good'
    case 'average': return 'Average'
    case 'fair': return 'Fair'
    case 'poor':
    default: return 'Poor'
  }
}

function filledForTone(tone: ScoreTone) {
  switch (tone) {
    case 'excellent': return 5
    case 'good': return 4
    case 'average': return 3
    case 'fair': return 2
    case 'poor':
    default: return 1
  }
}

export function buildHealthSnapshot(summary: HealthCashFlowMetrics | null): HealthSnapshotSummary | null {
  if (!summary) return null

  const salesGrowth = percentageDelta(summary.sales, summary.salesComp)
  const rows: HealthSnapshotRow[] = [
    {
      label: 'Gross Margin',
      metricKey: 'grossMargin' as HealthMetricKey,
      tone: toScoreTone(summary.grossMarginPct, { excellent: 45, good: 38, average: 32, fair: 26 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.grossMarginPct)} with ${formatSignedPointDelta(summary.grossMarginPct - summary.grossMarginPctComp)} vs LY.`,
    },
    {
      label: 'Sales Growth',
      metricKey: 'salesGrowth' as HealthMetricKey,
      tone: toScoreTone(salesGrowth, { excellent: 12, good: 5, average: 0, fair: -5 }),
      badge: '',
      filled: 0,
      caption: `${formatSignedPercent(salesGrowth)} vs LY revenue for the loaded KPI window.`,
    },
    {
      label: 'Markdowns',
      metricKey: 'markdowns' as HealthMetricKey,
      tone: toScoreTone(summary.markdownPct * 100, { excellent: 6, good: 10, average: 14, fair: 18 }, true),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.markdownPct * 100)} of sales with ${formatSignedPointDelta((summary.markdownPct - summary.markdownPctComp) * 100)} vs LY.`,
    },
    {
      label: 'Annual Turn',
      metricKey: 'turn' as HealthMetricKey,
      tone: toScoreTone(summary.turn, { excellent: 3.5, good: 2.5, average: 1.8, fair: 1.2 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatRatio(summary.turn)}× versus ${ReportService.formatRatio(summary.turnComp)}× in the comparison window.`,
    },
    {
      label: 'Inventory Freshness',
      metricKey: 'freshness' as HealthMetricKey,
      tone: toScoreTone(summary.freshness ?? 0, { excellent: 40, good: 28, average: 18, fair: 10 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.freshness ?? 0)} with ${formatSignedPointDelta((summary.freshness ?? 0) - (summary.freshnessComp ?? 0))} vs LY.`,
    },
  ].map((row) => ({
    ...row,
    badge: badgeForTone(row.tone),
    filled: filledForTone(row.tone),
  }))

  const score = Math.round((rows.reduce((acc, row) => acc + row.filled, 0) / (rows.length * 5)) * 100)

  return {
    score,
    compareLabel: 'Compare baseline: last year',
    rows,
  }
}

/* ------------------------------------------------------------------ *
 * v2 Health Score (Card-Next / Modal / History mockups)
 * The card/modal/history all read the weighted variance-vs-benchmark
 * score from `computeHealthScore` (healthScoreBenchmarks.ts) rather than
 * the legacy tone-segment `buildHealthSnapshot` above. `buildHealthSnapshot`
 * is kept intact for its existing consumers/tests.
 * ------------------------------------------------------------------ */

export interface HealthV2Band {
  label: string
  range: string
  pillBg: string
  pillText: string
  tone: ScoreTone
}

export interface HealthV2Metric {
  key: HealthMetricKey
  label: string
  points: number
  maxPoints: number
  actual: number
  benchmark: number
  lowerIsBetter: boolean
  tone: ScoreTone
  filled: number
  actualDisplay: string
  benchmarkDisplay: string
  captionSuffix: string
  weightLabel: string
  weightChipBg: string
  weightChipText: string
  description: string
  markerLeftPct: number
  benchmarkLeftPct: number
  dollarImpact?: { isExceeding: boolean; amount: number; display: string }
  // "Show LY" toggle: the same metric over the prior trailing 12 months. Null for sales
  // growth (needs 36 months of history; the summary carries 24).
  ly?: number | null
}

export interface HealthV2Snapshot {
  score: number
  band: HealthV2Band
  metrics: HealthV2Metric[]
  imu: { actual: number; benchmark: number | null; actualDisplay: string; benchmarkDisplay: string; lyDisplay: string }
}

export const HEALTH_BAND_DOT_COLOR: Record<ScoreTone, string> = {
  excellent: '#17B26A',
  good: '#85CB33',
  average: '#EAAA08',
  fair: '#F79009',
  poor: '#F04438',
}

export function getHealthScoreBand(score: number): HealthV2Band {
  // Band-pill bg/fg mirror the II card's HS_BANDPILL (overall score band chip — gauge + drawer).
  if (score >= 85) return { label: 'Excellent', range: '85–100', pillBg: '#D1FADF', pillText: '#027A48', tone: 'excellent' }
  if (score >= 70) return { label: 'Good', range: '70–84', pillBg: '#ECFDF3', pillText: '#027A48', tone: 'good' }
  if (score >= 55) return { label: 'Average', range: '55–69', pillBg: '#FEF7C3', pillText: '#854A0E', tone: 'average' }
  if (score >= 40) return { label: 'Fair', range: '40–54', pillBg: '#FEF0C7', pillText: '#B54708', tone: 'fair' }
  return { label: 'Poor', range: '0–39', pillBg: '#FEE4E2', pillText: '#B42318', tone: 'poor' }
}

const METRIC_KEY_BY_LABEL: Record<string, HealthMetricKey> = {
  'Gross Margin': 'grossMargin',
  'Sales Growth': 'salesGrowth',
  Markdowns: 'markdowns',
  Turnover: 'turn',
  'Freshness Factor': 'freshness',
}

const METRIC_META: Record<HealthMetricKey, { weightLabel: string; chipBg: string; chipText: string; description: string }> = {
  grossMargin: {
    weightLabel: 'HEAVIEST WEIGHT',
    chipBg: '#F4F3FF',
    chipText: '#5925DC',
    description: 'The percentage of revenue kept after cost of goods — the foundation of a healthy retail business.',
  },
  salesGrowth: {
    weightLabel: 'HIGH WEIGHT',
    chipBg: '#EFF8FF',
    chipText: '#175CD3',
    description: 'Year-over-year revenue change, benchmarked against peers in your vertical.',
  },
  markdowns: {
    weightLabel: 'MODERATE WEIGHT',
    chipBg: '#F2F4F7',
    chipText: '#475467',
    description: 'How much inventory is discounted to clear — lower is better. High markdowns signal over-buying or poor sell-through.',
  },
  turn: {
    weightLabel: 'MODERATE WEIGHT',
    chipBg: '#F2F4F7',
    chipText: '#475467',
    description: 'How many times your inventory sells through in a year. Higher turns mean fresher product and better cash flow.',
  },
  freshness: {
    weightLabel: 'LIGHTEST WEIGHT',
    chipBg: '#F2F4F7',
    chipText: '#475467',
    description: `Receipts over the last ${FRESHNESS_WINDOW_DAYS} days as a share of your current inventory, capped at ${FRESHNESS_CAP_PCT}%.`,
  },
  cashMargin: {
    weightLabel: 'MODERATE WEIGHT',
    chipBg: '#F2F4F7',
    chipText: '#475467',
    description: 'Cash kept after the cost of goods purchased.',
  },
}

function clamp01(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function scoreToneToCardTone(tone: ReturnType<typeof metricScoreTone>): ScoreTone {
  switch (tone) {
    case 'excellent': return 'excellent'
    case 'veryGood': return 'good'
    case 'average': return 'average'
    case 'belowAverage': return 'fair'
    case 'critical':
    default: return 'poor'
  }
}

function formatMetricValue(key: HealthMetricKey, value: number): string {
  if (key === 'turn') return `${value.toFixed(2)}×`
  if (key === 'salesGrowth') {
    const sign = value >= 0 ? '+' : '−'
    return `${sign}${Math.abs(value).toFixed(1)}%`
  }
  return `${value.toFixed(1)}%`
}

function formatImpactDollars(amount: number): string {
  return `$${Math.round(Math.abs(amount)).toLocaleString('en-US')}`
}

// What the score is benchmarked against: each metric is the 75th percentile (25th for
// markdowns) of the client's vertical. `source`: 'layer' = benchmark tables for every
// metric; 'pool' = some metrics from the ReportDataSource peer pool; 'default' = some
// metrics fell to the M1 default (too few peers); 'fallback' = peer feed unavailable,
// hand-set VERTICAL_BENCHMARKS table.
export type HealthBenchmarkContext = {
  benchmarks: ScoringBenchmarks
  // peer top-10% bars (P90; P10 for markdowns); missing ones are derived
  excellentAt: Partial<ScoringBenchmarks>
  imuPct: number | null
  // Cash Margin card benchmark (not scored), same source chain as the scored metrics.
  cashMarginPct: number | null
  source: 'layer' | 'pool' | 'default' | 'fallback'
}

export function fallbackBenchmarkContext(vertical: RetailVertical): HealthBenchmarkContext {
  const b = VERTICAL_BENCHMARKS[vertical]
  return { benchmarks: b, excellentAt: {}, imuPct: b.imuPct, cashMarginPct: b.cashMarginPct, source: 'fallback' }
}

// Benchmarks from /api/indie-insights/health, metric by metric; any value the feed lacks
// falls back to the hand-set default for `fallbackVertical`. Null when the feed has none.
export function benchmarkContextFromPeerFeed(feed: any, fallbackVertical: RetailVertical): HealthBenchmarkContext | null {
  const d = feed?.drivers
  const defaults = VERTICAL_BENCHMARKS[fallbackVertical]
  const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)
  const driverKeys: Array<[string, keyof ScoringBenchmarks]> = [
    ['gross_margin', 'grossMarginPct'],
    ['sales_growth', 'salesYoYPct'],
    ['markdowns', 'markdownPct'],
    ['annual_turn', 'inventoryTurn'],
    ['freshness', 'freshnessPct'],
  ]
  if (driverKeys.every(([driver]) => num(d?.[driver]?.benchmark) === null)) return null
  const benchmarks = { ...defaults } as ScoringBenchmarks
  const excellentAt: Partial<ScoringBenchmarks> = {}
  for (const [driver, key] of driverKeys) {
    benchmarks[key] = num(d?.[driver]?.benchmark) ?? defaults[key]
    const bar = num(d?.[driver]?.excellent_at)
    if (bar !== null) excellentAt[key] = bar
  }
  return {
    benchmarks,
    excellentAt,
    imuPct: num(feed?.imu?.benchmark) ?? defaults.imuPct,
    cashMarginPct: num(feed?.cash_margin?.benchmark) ?? defaults.cashMarginPct,
    source: feed?.benchmark_source === 'layer' || feed?.benchmark_source === 'pool' ? feed.benchmark_source : 'default',
  }
}

export function buildHealthV2Snapshot(
  summary: HealthCashFlowMetrics | null,
  context: HealthBenchmarkContext | null,
): HealthV2Snapshot | null {
  if (!summary || !context) return null

  const inputs: HealthScoreInputs = {
    grossMarginPct: summary.grossMarginPct,
    // no prior-year sales → growth isn't scored (as in GetHealthScoreV2.sql)
    salesYoYPct: summary.salesComp ? percentageDelta(summary.sales, summary.salesComp) : null,
    markdownPct: summary.markdownPct * 100,
    inventoryTurn: summary.turn,
    freshnessPct: summary.freshness,
  }

  const result = computeHealthScore(inputs, context.benchmarks, context.excellentAt)

  const lyByKey: Partial<Record<HealthMetricKey, number | null>> = {
    grossMargin: summary.grossMarginPctComp,
    markdowns: summary.markdownPctComp * 100,
    turn: summary.turnComp,
    freshness: summary.freshnessComp,
  }

  const metrics: HealthV2Metric[] = result.metrics.map((metric) => {
    const key = METRIC_KEY_BY_LABEL[metric.label] ?? 'grossMargin'
    const tone = scoreToneToCardTone(metricScoreTone(metric))
    const meta = METRIC_META[key]

    // Marker on the directional bar, on the same scale as the points: the benchmark tick
    // sits at BAR_BENCHMARK_POS, the zero-points shortfall at the far "worse" end.
    // Lower-is-better bars are drawn mirrored (green on the left).
    const position = metricBarPosition(metric)
    const markerLeftPct = clamp01(metric.lowerIsBetter ? 100 - position : position, 2, 98)
    const benchmarkLeftPct = metric.lowerIsBetter ? 100 - BAR_BENCHMARK_POS : BAR_BENCHMARK_POS

    let captionSuffix = ''
    if (key === 'markdowns') captionSuffix = ' · lower is better'
    if (key === 'freshness') captionSuffix = ` · ${FRESHNESS_WINDOW_DAYS}-day window`

    let dollarImpact: HealthV2Metric['dollarImpact']
    if (key === 'salesGrowth') {
      const insight = computeMetricInsight({
        metricKey: 'salesGrowth',
        actual: metric.actual,
        benchmark: metric.benchmark,
        totalSales: summary.sales,
        currentInventory: summary.invAvgCost,
        currentCogs: summary.sales * (1 - summary.grossMarginPct / 100),
        totalInventoryValue: summary.invAvgCost,
      })
      // Monthly revenue delta vs benchmark (keeps the mockup's "a month" framing).
      const monthly = (summary.sales * Math.abs(metric.actual - metric.benchmark)) / 100 / 12
      dollarImpact = { isExceeding: insight.isExceeding, amount: monthly, display: formatImpactDollars(monthly) }
    }

    return {
      key,
      label: healthMetricDisplayLabel(metric.label),
      points: metric.score,
      maxPoints: metric.weight,
      actual: metric.actual,
      benchmark: metric.benchmark,
      lowerIsBetter: metric.lowerIsBetter,
      tone,
      filled: filledForTone(tone),
      actualDisplay: formatMetricValue(key, metric.actual),
      benchmarkDisplay: formatMetricValue(key, metric.benchmark),
      captionSuffix,
      weightLabel: meta.weightLabel,
      weightChipBg: meta.chipBg,
      weightChipText: meta.chipText,
      description: meta.description,
      markerLeftPct,
      benchmarkLeftPct,
      dollarImpact,
      ly: lyByKey[key] ?? null,
    }
  })

  return {
    score: result.totalScore,
    band: getHealthScoreBand(result.totalScore),
    metrics,
    imu: {
      actual: summary.imuPct,
      benchmark: context.imuPct,
      actualDisplay: `${summary.imuPct.toFixed(1)}%`,
      benchmarkDisplay: context.imuPct != null ? `${context.imuPct.toFixed(1)}%` : '—',
      lyDisplay: `${summary.imuPctComp.toFixed(1)}%`,
    },
  }
}

export function buildCashFlowRows(summary: HealthCashFlowMetrics | null): CashFlowSummaryRow[] {
  if (!summary) return []

  return [
    {
      label: 'Revenue',
      dotColor: '#374151',
      value: PerformanceDashboardService.moneyFormat(summary.sales, true),
      sub: `LY ${PerformanceDashboardService.moneyFormat(summary.salesComp, true)}`,
      delta: formatSignedPercent(percentageDelta(summary.sales, summary.salesComp)),
      positive: summary.sales >= summary.salesComp,
    },
    {
      label: 'Purchases',
      dotColor: '#7F22FE',
      value: PerformanceDashboardService.moneyFormat(summary.inflowCost, true),
      sub: `${ReportService.formatPercent(summary.costOfPurchasesPct)} of revenue`,
      delta: formatSignedPointDelta(summary.costOfPurchasesPct - summary.costOfPurchasesPctComp),
      positive: summary.costOfPurchasesPct <= summary.costOfPurchasesPctComp,
    },
    {
      label: 'Cash Margin',
      dotColor: '#374151',
      value: PerformanceDashboardService.moneyFormat(summary.cashMargin, true),
      sub: `${ReportService.formatPercent(summary.cashMarginPct)} of revenue`,
      delta: formatSignedPointDelta(summary.cashMarginPct - summary.cashMarginPctComp),
      positive: summary.cashMarginPct >= summary.cashMarginPctComp,
      strong: true,
    },
    {
      label: 'Gross Margin',
      dotColor: '#FFB900',
      value: PerformanceDashboardService.moneyFormat(summary.grossMargin, true),
      sub: `${ReportService.formatPercent(summary.grossMarginPct)} of revenue`,
      delta: formatSignedPointDelta(summary.grossMarginPct - summary.grossMarginPctComp),
      positive: summary.grossMarginPct >= summary.grossMarginPctComp,
    },
    {
      label: 'Markdowns',
      dotColor: '#00C950',
      value: PerformanceDashboardService.moneyFormat(summary.markdowns, true),
      sub: `${ReportService.formatPercent(summary.markdownPct * 100)} of revenue`,
      delta: formatSignedPointDelta((summary.markdownPct - summary.markdownPctComp) * 100),
      positive: summary.markdownPct <= summary.markdownPctComp,
    },
  ]
}

/**
 * Calculates CMROI/GMROI using the same ReportService formulas as the Inventory Performance
 * report. Callers are responsible for supplying an `invAvgCost` that is weighted the same way
 * the report weights it (per-row/class cost-complement applied to that row's average inventory,
 * then summed) rather than a single blended cost-complement applied to the client total average
 * inventory, otherwise the results will diverge from the report when margins vary by class.
 */
export function calculateRoiMetrics({ sales, inflowCost, markdowns, costComplement, imu, invAvgCost }: { sales: number; inflowCost: number; markdowns: number; costComplement: number; imu: number; invAvgCost: number }): { cmroi: number; gmroi: number } {
  return {
    cmroi: ReportService.calculateCmroi({ sales, inflowCost, invAvgCost, format: false }) as number,
    gmroi: ReportService.calculateGmroi({ sales, markdowns, costComplement, imu, invAvgCost, format: false }) as number,
  }
}

export function buildRoiSummaryCards(summary: HealthCashFlowMetrics | null): RoiSummaryCard[] {
  if (!summary) return []

  return [
    {
      label: 'GMROI',
      value: `${ReportService.formatRatio(summary.gmroi)}×`,
      helper: `LY ${ReportService.formatRatio(summary.gmroiComp)}×`,
      delta: formatSignedRatio(summary.gmroi - summary.gmroiComp),
      positive: summary.gmroi >= summary.gmroiComp,
    },
    {
      label: 'CMROI',
      value: `${ReportService.formatRatio(summary.cmroi)}×`,
      helper: `LY ${ReportService.formatRatio(summary.cmroiComp)}×`,
      delta: formatSignedRatio(summary.cmroi - summary.cmroiComp),
      positive: summary.cmroi >= summary.cmroiComp,
    },
  ]
}

function CashDonut() {
  return (
    <svg viewBox="0 0 173 173" className="h-full w-full" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M86.5 0C104.767 0 122.565 5.78295 137.343 16.52L120.873 39.1896C110.882 31.9307 98.8495 28.0211 86.5 28.0211V0Z" fill="#7C3AED" />
      <path d="M137.343 16.52C150.249 25.8967 160.311 38.6597 166.415 53.3979C172.52 68.1361 174.43 84.2754 171.935 100.032C169.439 115.788 162.635 130.547 152.275 142.677C141.915 154.808 128.402 163.837 113.23 168.766L104.571 142.117C114.828 138.784 123.963 132.68 130.968 124.479C137.972 116.278 142.572 106.3 144.259 95.6481C145.946 84.9961 144.654 74.085 140.527 64.1211C136.4 54.1572 129.598 45.5287 120.873 39.1896L137.343 16.52Z" fill="#F59E0B" />
      <path d="M113.23 168.766C101.338 172.63 88.7501 173.87 76.333 172.4C63.9159 170.931 51.9651 166.787 41.3039 160.253C30.6427 153.72 21.5246 144.953 14.5779 134.557C7.63117 124.16 3.02098 112.381 1.06496 100.032C-0.891055 87.6818 -0.146398 75.0547 3.24763 63.0204C6.64165 50.9861 12.6044 39.8307 20.7249 30.3227C28.8454 20.8148 38.9307 13.1804 50.2859 7.94562C61.6411 2.7108 73.9962 0 86.5 0V28.0211C78.0468 28.0211 69.694 29.8538 62.0173 33.3928C54.3405 36.9318 47.5223 42.0931 42.0323 48.521C36.5424 54.9489 32.5113 62.4906 30.2167 70.6265C27.9222 78.7623 27.4187 87.299 28.7411 95.6481C30.0635 103.997 33.1802 111.961 37.8766 118.989C42.573 126.018 48.7373 131.945 55.9449 136.361C63.1525 140.778 71.2319 143.58 79.6265 144.574C88.0212 145.567 96.5315 144.729 104.571 142.117L113.23 168.766Z" fill="#22C55E" />
    </svg>
  )
}

function getPreviousEpochMonth(epochMonth: number): number {
  return epochMonth - 1;
}

function getRoiValueColor(value: number, benchmark: number): string {
  if (value > 0 && benchmark > 0) {
    const roundedValue = Math.round(value * 10) / 10
    const roundedBenchmark = Math.round(benchmark * 10) / 10

    if (roundedValue < roundedBenchmark) return 'text-red-500'
    if (roundedValue > roundedBenchmark) return 'text-green-600'
  }

  return 'text-gray-900'
}

export interface HealthScoreSummaryForHost {
  verticalAverage: number | null
  verticalLabel: string | null
  // Ready-to-send Ask Indie prompt covering the whole score; null until data has loaded.
  askIndiePrompt: string | null
}

export function HealthCashFlowSection({
  simpleHealthView = false,
  filterParams,
  onSummaryChange,
}: {
  simpleHealthView?: boolean;
  // SPS-5572: location-only filter (whole-store benchmark, so no category/class). The
  // home simple view passes nothing, so it stays unfiltered.
  filterParams?: Pick<DashboardFilterRequestParams, 'locations' | 'locationExclude'>;
  // Lets the host tile (home dashboard Performance widget) show the vertical average and an
  // Ask Indie link without re-fetching.
  onSummaryChange?: (summary: HealthScoreSummaryForHost) => void;
}) {
  const currentClientContext = CurrentClientProvider.useClient() as any
  const currentClient = currentClientContext?.client

  const [apiData, setApiData] = React.useState<HealthCashFlowApiData | null>(null)
  const [previousApiData, setPreviousApiData] = React.useState<HealthCashFlowApiData | null>(null)
  const [summaryLoading, setLoading] = React.useState(true)

  // SPS-5572: stable primitive key for the location filter so the effect re-fetches when
  // the selection changes (both the current and prior-month fetches must respect it).
  const filterLocations = filterParams?.locations ?? []
  const filterLocationExclude = filterParams?.locationExclude ?? false
  const filterKey = `${filterLocations.join(',')}|${filterLocationExclude ? 1 : 0}`

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setApiData(null)
      setPreviousApiData(null)
      setLoading(false)
      return () => { cancelled = true }
    }

    setLoading(true)

    const endEpoch = EpochMonthService.getTodayEpochMonth()
    const prevEndEpoch = getPreviousEpochMonth(endEpoch)
    const locations = filterParams?.locations ?? []
    const locationExclude = filterParams?.locationExclude ?? false

    Promise.all([
      ClientDataService.getHealthCashFlowSummary({
        clientId: currentClient.Id,
        endEpochMonth: endEpoch,
        locations,
        locationExclude,
      }),
      ClientDataService.getHealthCashFlowSummary({
        clientId: currentClient.Id,
        endEpochMonth: prevEndEpoch,
        locations,
        locationExclude,
      }),
    ])
      .then(([currentResponse, previousResponse]) => {
        if (cancelled) return
        setApiData(currentResponse?.data ?? null)
        setPreviousApiData(previousResponse?.data ?? null)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        console.error(error)
        setApiData(null)
        setPreviousApiData(null)
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [currentClient?.Id, filterKey])

  const summary = React.useMemo(() => deriveHealthCashFlowMetrics(apiData), [apiData])
  const previousSummary = React.useMemo(() => deriveHealthCashFlowMetrics(previousApiData), [previousApiData])

  // SPS-5182 cash-margin rows (kept). The legacy buildHealthSnapshot memos were
  // removed — the v2 Health Score uses healthSnapshot / previousHealthSnapshot
  // (declared below), so re-adding the old ones here would duplicate-declare.
  const cashFlowRows = React.useMemo(() => buildCashFlowRows(summary), [summary])

  const [isEditingCashCard, setIsEditingCashCard] = React.useState(false)
  // SPS-5569 header toggles: Show IMU adds the IMU margin sub-line under Gross Margin; Show LY
  // overlays a per-driver last-year line. Both re-render the driver rows.
  const [showIMU, setShowIMU] = React.useState(true)
  const [showLY, setShowLY] = React.useState(false)
  const [isScoreDrawerOpen, setIsScoreDrawerOpen] = React.useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false)
  const [historySeries, setHistorySeries] = React.useState<MonthlyScorePoint[] | null>(null)
  // SPS-5568: load status for the lazy/cached history fetch — splits a genuine empty feed
  // ('loaded' with no rows) from a real fetch failure ('error') for the modal's messaging.
  const [historyStatus, setHistoryStatus] = React.useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  // The scope signature (client · vertical · location filter) the cached series belongs to.
  // Set ONLY on a successful fetch; a mismatch (or null) means the next open must refetch.
  const loadedHistoryKeyRef = React.useRef<string | null>(null)
  const [hoveredDonutSegment, setHoveredDonutSegment] = React.useState<'purchases' | 'expenses' | 'cashFlow' | null>(null)
  const [revenueInput, setRevenueInput] = React.useState('0')
  const [purchasesPctInput, setPurchasesPctInput] = React.useState('0.0')
  const [expensesPctInput, setExpensesPctInput] = React.useState('40.0')

  // SPS-5182 cash-margin lookup (kept). Old scoreChange / displayScore removed —
  // the v2 Health Score re-declares both below from healthSnapshot.
  const cashMarginValue = cashFlowRows.find((row) => row.label === 'Cash Margin')

  const clientVertical = React.useMemo(() => resolveClientVertical(currentClient), [currentClient])
  const verticalBenchmark = VERTICAL_BENCHMARKS[clientVertical]
  // Operating expenses aren't in any data source, so this stays a labelled estimate.
  const benchmarkExpensesPct = 40

  // Peer feed GET /api/indie-insights/health: the cross-client fields (avg-in-vertical,
  // retailer count n, percentile) AND the vertical-pool benchmarks the score is measured
  // against (SPS-5570). Fetched once per client. If the feed fails or has no benchmarks,
  // the score falls back to the hand-set VERTICAL_BENCHMARKS table.
  const [peerHealth, setPeerHealth] = React.useState<
    { avg: number | null; n: number | null; percentile: number | null; quantiles: number[] | null; vertical: string | null; benchmarks: HealthBenchmarkContext | null } | null
  >(null)
  const [peerStatus, setPeerStatus] = React.useState<'loading' | 'done'>('loading')

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setPeerHealth(null)
      setPeerStatus('done')
      return () => { cancelled = true }
    }

    setPeerStatus('loading')
    ClientDataService.getIndieInsightsHealth(currentClient.Id)
      .then((res: any) => {
        if (cancelled) return
        // Request.get already unwraps to response.data, so `res` IS the health
        // object { client_id, vertical, avg_in_vertical, n_in_vertical, percentile_in_vertical, drivers, ... }.
        const d = res ?? null
        setPeerHealth(d ? {
          avg: d.avg_in_vertical ?? null,
          n: d.n_in_vertical ?? null,
          percentile: d.percentile_in_vertical ?? null,
          quantiles: Array.isArray(d.peer_score_quantiles) ? d.peer_score_quantiles.map(Number) : null,
          vertical: d.vertical ?? null,
          benchmarks: benchmarkContextFromPeerFeed(d, resolveClientVertical(currentClient, d.vertical)),
        } : null)
        setPeerStatus('done')
      })
      .catch(() => {
        if (cancelled) return
        setPeerHealth(null)
        setPeerStatus('done')
      })

    return () => { cancelled = true }
  }, [currentClient?.Id])

  const benchmarkContext: HealthBenchmarkContext | null = React.useMemo(() => {
    if (peerStatus === 'loading') return null
    return peerHealth?.benchmarks ?? fallbackBenchmarkContext(resolveClientVertical(currentClient, peerHealth?.vertical))
  }, [peerStatus, peerHealth, currentClient])

  // Cash Margin card benchmarks come from the same source as the Health Score: cash margin
  // is the vertical's 75th percentile (benchmark tables → peer pool → M1 default), purchases
  // its complement, and GMROI/CMROI use the same gross/cash margin benchmarks.
  const benchmarkCashMarginPct = benchmarkContext?.cashMarginPct ?? verticalBenchmark.cashMarginPct
  const benchmarkGrossMarginPct = benchmarkContext?.benchmarks.grossMarginPct ?? verticalBenchmark.grossMarginPct
  const benchmarkPurchasesPct = Math.max(0, 100 - benchmarkCashMarginPct)

  // Hold the score until the benchmarks are known, so it never flips fallback → pool.
  const loading = summaryLoading || peerStatus === 'loading'

  // v2 scoring — the card/modal/history all read the weighted variance-vs-benchmark
  // score from computeHealthScore (via buildHealthV2Snapshot), not the legacy snapshot.
  const healthSnapshot = React.useMemo(() => buildHealthV2Snapshot(summary, benchmarkContext), [summary, benchmarkContext])
  const previousHealthSnapshot = React.useMemo(() => buildHealthV2Snapshot(previousSummary, benchmarkContext), [previousSummary, benchmarkContext])

  const scoreChange: number | null =
    healthSnapshot && previousHealthSnapshot ? healthSnapshot.score - previousHealthSnapshot.score : null

  // SPS-5568: scope signature for the history cache. Any change (client, vertical, or the
  // location filter) must invalidate the cache so the next open refetches for the new scope.
  const historySignature = `${currentClient?.Id ?? ''}|${benchmarkContext?.source ?? 'pending'}|${filterKey}`

  // SPS-5568: scope changed → drop the cached/displayed history so a stale series never
  // shows and the next open (or an already-open modal) refetches for the new scope.
  React.useEffect(() => {
    loadedHistoryKeyRef.current = null
    setHistorySeries(null)
    setHistoryStatus('idle')
  }, [historySignature])

  // Score-history series (SPS-5571 + SPS-5568): one GET /api/clients/{id}/health-score-history
  // call returns up to 12 trailing-12 snapshots (one row per epochMonthRm). Each row carries
  // the same shape as the single-month summary, so we score each with the SAME
  // buildHealthV2Snapshot pipeline the card uses — the modal's newest point therefore equals
  // the card. Rows come back oldest → newest (ORDER BY rm).
  //
  // SPS-5568 lazy + cached: this runs ONLY while the modal is OPEN — never on card/page
  // render. A successful fetch is cached against the scope signature (served on reopen with
  // no refetch). A FAILED fetch is NOT cached (loadedHistoryKeyRef stays null), so simply
  // reopening retries. The SPS-5572 location filter is forwarded so a store-scoped card
  // scopes its history to the same store.
  React.useEffect(() => {
    if (!isHistoryOpen) return

    if (!currentClient?.Id) {
      setHistorySeries(null)
      setHistoryStatus('idle')
      return
    }

    // Benchmarks still loading: wait (the signature changes once they arrive).
    if (!benchmarkContext) {
      setHistoryStatus('loading')
      return
    }

    // Serve the cache: this exact scope already loaded successfully.
    if (loadedHistoryKeyRef.current === historySignature) return

    let cancelled = false
    setHistoryStatus('loading')

    const endEpoch = EpochMonthService.getTodayEpochMonth()

    ClientDataService.getHealthScoreHistory({
      clientId: currentClient.Id,
      endEpochMonth: endEpoch,
      months: 12,
      locations: filterLocations,
      locationExclude: filterLocationExclude,
    })
      .then((res: any) => {
        if (cancelled) return

        const rows: HealthCashFlowApiData[] = Array.isArray(res?.data) ? res.data : []
        const built: MonthlyScorePoint[] = []
        for (const row of rows) {
          const anchor = Number((row as any).epochMonthRm)
          if (!Number.isFinite(anchor)) continue

          const monthMetrics = deriveHealthCashFlowMetrics(row)
          // Every point is scored against today's benchmarks (the pool is only computed for
          // the current month); the window ends the month before the snapshot month.
          const snapshot = buildHealthV2Snapshot(monthMetrics, benchmarkContext)
          if (!snapshot) continue

          const date = EpochMonthService.epochMonthToDate(anchor - 1)
          const metricMap: MonthlyScorePoint['metrics'] = {}
          for (const metric of snapshot.metrics) {
            metricMap[metric.key as HistoryMetricKey] = {
              actual: metric.actual,
              benchmark: metric.benchmark,
              pts: metric.points,
              max: metric.maxPoints,
            }
          }

          built.push({
            epochMonth: anchor,
            label: EpochMonthService.epochMonthToMMM(anchor - 1),
            year: date.getFullYear(),
            score: snapshot.score,
            band: snapshot.band.label,
            metrics: metricMap,
          })
        }

        // Success (even with zero rows) caches this scope — zero rows is a genuine
        // "no data yet", distinct from the error path below.
        setHistorySeries(built)
        setHistoryStatus('loaded')
        loadedHistoryKeyRef.current = historySignature
        console.debug('[health-series] built', built.length)
      })
      .catch(() => {
        if (cancelled) return
        // Do NOT cache a failure — leave the key null so reopening retries.
        setHistorySeries(null)
        setHistoryStatus('error')
        loadedHistoryKeyRef.current = null
      })

    return () => { cancelled = true }
    // filterLocations is a fresh array each render but its content is captured in filterKey
    // (→ historySignature), so depending on the signature is sufficient and avoids re-fetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryOpen, historySignature, currentClient?.Id])

  // No snapshot → needle rests at 0 and the number reads "—" (never a made-up score).
  const displayScore = loading ? 0 : healthSnapshot?.score ?? 0

  // Trailing-12 end month label ("Sep 2026"): the last CLOSED month, i.e. the month before
  // the current snapshot month (the window is spans rm−12..rm−1).
  const throughLabel = React.useMemo(() => {
    const date = EpochMonthService.epochMonthToDate(EpochMonthService.getTodayEpochMonth() - 1)
    return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`
  }, [])

  const clientLabel = [currentClient?.Name, currentClient?.ClientCode ? `(${currentClient.ClientCode})` : '']
    .filter(Boolean)
    .join(' ')
    .trim()

  const avgVerticalScore: number | null = peerHealth?.avg ?? null
  const verticalRetailerCount: number | null = peerHealth?.n ?? null
  const verticalPercentile: number | null = peerHealth?.percentile ?? null
  // Real vertical from the peer feed only. When peer data is genuinely absent we do NOT
  // assert the resolveClientVertical default ("Apparel Contemporary Fashion") — show the
  // neutral awaiting state without a vertical name instead.
  // 'Unassigned' (no vertical anywhere) is not shown as a vertical name.
  const peerVerticalLabel: string | null =
    peerHealth?.vertical && peerHealth.vertical !== 'Unassigned' ? peerHealth.vertical : null

  // SPS-5570: percentile-chip values. Normalize the raw peer-feed percentile to a clean
  // integer 0–100 (or null → percentile box shows its awaiting-feed fallback), and prefer the
  // peer vertical label, else the already-resolved client vertical. SPS-5569 Card-Next shows the
  // cohort count (N) on the "Avg in <vertical>" tile — but only when the peer feed returns it.
  // Place the card's OWN score (location-filtered or not) in the vertical's score
  // distribution, so score, average and percentile always describe the same number.
  const normalizedPercentile = (healthSnapshot && percentileFromQuantiles(healthSnapshot.score, peerHealth?.quantiles))
    ?? normalizePercentile(verticalPercentile)

  const scoreChangeLabel =
    scoreChange === null ? '—' : scoreChange === 0 ? '0 pts' : `${scoreChange > 0 ? '+' : '−'}${Math.abs(scoreChange)} pts`
  const scoreChangeColor = scoreChange === null || scoreChange === 0 ? '#101828' : scoreChange > 0 ? '#027A48' : '#B42318'

  // SPS-5569 Interactive-card derived display values.
  // vertical label used by the narrative + "Avg in <vertical>" tile (peer feed, else neutral).
  const healthVertical = peerVerticalLabel ?? 'your vertical'
  // What "benchmark" means in Ask Indie prompts and the drawer.
  const benchmarkBasis = benchmarkContext?.source === 'layer' || benchmarkContext?.source === 'pool'
    ? `the top 25% of ${healthVertical} retailers`
    : `M1's benchmark for ${healthVertical}`
  // "Ask Indie" on a metric row: opens the Indie chat with the actual, benchmark and LY
  // filled in (same bridge as the Key Metrics "What's driving this?" link).
  const askIndieAboutMetric = (metric: HealthV2Metric) => {
    const ly = metric.ly != null ? `, last year ${formatMetricValue(metric.key, metric.ly)}` : ''
    const scope = isLocationFiltered ? ` for location ${filterLocations.join(', ')}` : ''
    requestOrbitIndiePanelOpen({
      source: 'health-score',
      prompt: `Health Score check — ${metric.label}${scope}: ${metric.actualDisplay} over the trailing 12 months through ${throughLabel}, `
        + `vs a benchmark of ${metric.benchmarkDisplay} (${benchmarkBasis})${ly}. `
        + `What's driving this and what should I do about it?`,
      submit: true,
    })
  }

  // Shown as an ordinal ("30th"): "Top 70%" read as good for a below-median store.
  const percentileLabel = normalizedPercentile !== null ? formatOrdinal(normalizedPercentile) : '—'
  // Peers are always whole stores; with a location filter on, the card's score is that
  // location's, ranked against them — labelled so.
  const isLocationFiltered = filterLocations.length > 0
  // Band dot color = the band's gauge arc color.
  const bandDotColor = healthSnapshot ? HEALTH_BAND_DOT_COLOR[healthSnapshot.band.tone] : '#98A2B3'

  React.useEffect(() => {
    if (!summary) {
      setRevenueInput('0')
      setPurchasesPctInput('0.0')
      return
    }

    setRevenueInput(String(Math.round(summary.sales)))
    setPurchasesPctInput(summary.costOfPurchasesPct.toFixed(1))
  }, [summary?.sales, summary?.costOfPurchasesPct])

  const revenueDollars = Math.max(0, Number(revenueInput) || 0)
  const purchasesPct = Math.min(100, Math.max(0, Number(purchasesPctInput) || 0))
  const purchasesDollars = (purchasesPct / 100) * revenueDollars
  const cashMarginPct = Math.max(0, 100 - purchasesPct)
  const cashMarginDollars = (cashMarginPct / 100) * revenueDollars
  const expensesPct = Math.min(100, Math.max(0, Number(expensesPctInput) || 0))
  const expensesDollars = (expensesPct / 100) * revenueDollars
  const netCashFlowDollars = Math.max(0, cashMarginDollars - expensesDollars)
  const netCashFlowPct = Math.max(0, cashMarginPct - expensesPct)

  const purchasesDeltaVsBenchmark = purchasesPct - benchmarkPurchasesPct
  const cashMarginDeltaVsBenchmark = cashMarginPct - benchmarkCashMarginPct

  const donutHasData = purchasesPct > 0 || expensesPct > 0 || netCashFlowPct > 0
  const donutStops = donutHasData
    ? `#3B82F6 0 ${Math.max(0, purchasesPct)}%, #F59E0B ${Math.max(0, purchasesPct)}% ${Math.max(0, purchasesPct + expensesPct)}%, #22C55E ${Math.max(0, purchasesPct + expensesPct)}% 100%`
    : '#E5E7EB 0 100%'

  const activeDonutTooltip = React.useMemo(() => {
    if (!hoveredDonutSegment) return null
    if (hoveredDonutSegment === 'purchases') {
      return {
        title: 'Purchases',
        val: PerformanceDashboardService.moneyFormat(purchasesDollars, true),
        pct: purchasesPct,
        color: '#3B82F6',
      }
    }
    if (hoveredDonutSegment === 'expenses') {
      return {
        title: 'Expenses',
        val: PerformanceDashboardService.moneyFormat(expensesDollars, true),
        pct: expensesPct,
        color: '#F59E0B',
      }
    }
    return {
      title: 'Cash Flow',
      val: PerformanceDashboardService.moneyFormat(cashMarginDollars, true),
      pct: cashMarginPct,
      color: '#22C55E',
    }
  }, [hoveredDonutSegment, purchasesDollars, purchasesPct, expensesDollars, expensesPct, cashMarginDollars, cashMarginPct])

  const roiCards = React.useMemo(() => {
    if (!summary) return []

    const invAvgCost = summary.invAvgCost || 0
    const dynamicCmroi = invAvgCost ? cashMarginDollars / invAvgCost : 0
    const dynamicGrossMargin = revenueDollars * (summary.grossMarginPct / 100)
    const dynamicGmroi = invAvgCost ? dynamicGrossMargin / invAvgCost : 0
    const cmroiBenchmark = invAvgCost ? (revenueDollars * (benchmarkCashMarginPct / 100)) / invAvgCost : 0
    const gmroiBenchmark = invAvgCost ? (revenueDollars * (benchmarkGrossMarginPct / 100)) / invAvgCost : 0

    return [
      {
        label: 'GMROI',
        value: dynamicGmroi,
        benchmark: gmroiBenchmark,
        ly: summary.gmroiComp,
        grossMarginDollars: dynamicGrossMargin,
        grossMarginPct: summary.grossMarginPct,
        invAvgCost,
      },
      {
        label: 'CMROI',
        value: dynamicCmroi,
        benchmark: cmroiBenchmark,
        ly: summary.cmroiComp,
        grossMarginDollars: dynamicGrossMargin,
        grossMarginPct: summary.grossMarginPct,
        invAvgCost,
      },
    ]
  }, [summary, cashMarginDollars, revenueDollars, benchmarkCashMarginPct, benchmarkGrossMarginPct])

  // "Ask Indie" on GMROI / CMROI: what the metric means, its value, benchmark and LY, plus
  // every figure in the Cash Margin table as currently shown (including any edits).
  const askIndieAboutRoi = (card: (typeof roiCards)[number]) => {
    const money = (value: number) => PerformanceDashboardService.moneyFormat(value, true)
    const definition = card.label === 'GMROI'
      ? 'GMROI (gross margin return on inventory) = gross margin dollars ÷ average inventory at cost; it shows how many dollars of gross margin each dollar invested in inventory earns per year'
      : 'CMROI (cash margin return on inventory) = cash margin dollars (sales minus purchases at cost) ÷ average inventory at cost; it shows how many dollars of cash each dollar invested in inventory returns per year'
    const scope = isLocationFiltered ? ` for location ${filterLocations.join(', ')}` : ''
    const ly = card.ly ? `, last year ${card.ly.toFixed(1)}×` : ''
    requestOrbitIndiePanelOpen({
      source: 'health-score',
      prompt: `Cash Margin check — ${card.label}${scope}, trailing 12 months through ${throughLabel}. `
        + `${definition}. `
        + `My ${card.label} is ${card.value.toFixed(1)}× vs a benchmark of ${card.benchmark.toFixed(1)}× (based on ${benchmarkBasis})${ly}. `
        + `My cash margin table: revenue ${money(revenueDollars)}; `
        + `purchases ${money(purchasesDollars)} (${purchasesPct.toFixed(1)}% of revenue, benchmark ${benchmarkPurchasesPct.toFixed(1)}%); `
        + `cash margin ${money(cashMarginDollars)} (${cashMarginPct.toFixed(1)}%, benchmark ${benchmarkCashMarginPct.toFixed(1)}%); `
        + `gross margin ${money(card.grossMarginDollars)} (${card.grossMarginPct.toFixed(1)}%, benchmark ${benchmarkGrossMarginPct.toFixed(1)}%); `
        + `expenses ${money(expensesDollars)} (${expensesPct.toFixed(1)}%, estimated); `
        + `net cash flow ${money(netCashFlowDollars)} (${netCashFlowPct.toFixed(1)}%); `
        + `average inventory at cost ${money(card.invAvgCost)}. `
        + `What's driving my ${card.label} and what should I do to improve it?`,
      submit: true,
    })
  }
  // One prompt for the whole score: headline, every metric with benchmark and last year,
  // IMU, and the cash-flow figures (Home dashboard Performance widget's Ask Indie link).
  const scoreAskIndiePrompt: string | null = React.useMemo(() => {
    if (loading || !healthSnapshot || !summary) return null
    const money = (value: number) => PerformanceDashboardService.moneyFormat(value, true)
    const metricLines = healthSnapshot.metrics.map((metric) => {
      const ly = metric.ly != null ? `, last year ${formatMetricValue(metric.key, metric.ly)}` : ''
      return `${metric.label} ${metric.actualDisplay} vs benchmark ${metric.benchmarkDisplay}${ly} — ${metric.points.toFixed(1)} of ${metric.maxPoints} pts (${badgeForTone(metric.tone)})`
    })
    const roiLines = roiCards.map((card) => `${card.label} ${card.value.toFixed(1)}× vs benchmark ${card.benchmark.toFixed(1)}×${card.ly ? `, last year ${card.ly.toFixed(1)}×` : ''}`)
    const peers = avgVerticalScore !== null
      ? ` The ${healthVertical} average is ${avgVerticalScore}${normalizedPercentile !== null ? ` and I'm at the ${formatOrdinal(normalizedPercentile)} percentile` : ''}.`
      : ''
    return `Health Score check — trailing 12 months through ${throughLabel}. `
      + `My RetailORBIT Health Score is ${healthSnapshot.score}/100 (${healthSnapshot.band.label}), ${scoreChangeLabel === '—' ? 'no prior month to compare' : `${scoreChangeLabel} vs last month`}.${peers} `
      + `Benchmarks are ${benchmarkBasis}; points come from gross margin (30), sales growth (25), markdowns (20), annual turn (15) and inventory freshness (10). `
      + `Metrics: ${metricLines.join('; ')}. `
      + `IMU ${healthSnapshot.imu.actualDisplay} vs benchmark ${healthSnapshot.imu.benchmarkDisplay}, last year ${healthSnapshot.imu.lyDisplay} (not scored). `
      + `Cash flow: revenue ${money(summary.sales)} (last year ${money(summary.salesComp)}); purchases ${money(summary.inflowCost)} (${summary.costOfPurchasesPct.toFixed(1)}% of revenue, benchmark ${benchmarkPurchasesPct.toFixed(1)}%); `
      + `cash margin ${summary.cashMarginPct.toFixed(1)}% (benchmark ${benchmarkCashMarginPct.toFixed(1)}%, last year ${summary.cashMarginPctComp.toFixed(1)}%); ${roiLines.join('; ')}. `
      + `Explain what's driving my score and the one or two changes that would move it most.`
  }, [loading, healthSnapshot, summary, roiCards, avgVerticalScore, healthVertical, benchmarkBasis, normalizedPercentile, throughLabel, scoreChangeLabel, benchmarkPurchasesPct, benchmarkCashMarginPct])

  React.useEffect(() => {
    onSummaryChange?.({
      verticalAverage: avgVerticalScore,
      verticalLabel: peerVerticalLabel,
      askIndiePrompt: scoreAskIndiePrompt,
    })
    // onSummaryChange is a host callback; re-reporting only when the values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avgVerticalScore, peerVerticalLabel, scoreAskIndiePrompt])

  // Simple view
  if (simpleHealthView) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4">
        <div className="w-full max-w-[260px]">
          <ClientDashboardV3ScoreGauge className="w-full h-auto" score={displayScore} />
        </div>
        <div className="flex flex-col items-center -mt-2">
          <span className="text-5xl font-semibold leading-none tracking-tighter text-gray-900">
            {loading ? '—' : healthSnapshot?.score ?? '—'}
          </span>
          <span className="text-sm text-gray-500">/100</span>
        </div>
        {/* Month-over-month change (the dial already shows the band). */}
        {!loading && healthSnapshot && (
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500">
              <span className="font-semibold" style={{ color: scoreChangeColor }}>{scoreChangeLabel}</span> vs last month
            </span>
          </div>
        )}
      </div>
    )
  }

  // Full view
  return (
    <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-3.5 rounded-2xl border border-gray-200 bg-white px-5 py-[24px] shadow-sm">
        {/* Header */}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <h3 className="text-base font-bold text-gray-900">RetailORBIT Health Score<sup className="text-[9px]">™</sup></h3>
              <KpiHelpIcon tooltipKey="retailOrbitHealthScore" />
            </div>
            <p className="text-xs text-gray-500">The independent specialty retailer health score</p>
          </div>
          <span className="flex-grow" />
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-[3px] text-[11px] font-medium"
              style={{ background: '#F9FAFB', borderColor: '#EAECF0', color: '#475467' }}
            >
              <Calendar className="h-3 w-3" />
              Trailing 12 months · through {throughLabel}
            </span>
            <div className="flex gap-4 text-xs" style={{ color: '#344054' }}>
              <label className="inline-flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" className="cursor-pointer" checked={showIMU} onChange={(e) => setShowIMU(e.target.checked)} />
                Show IMU
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" className="cursor-pointer" checked={showLY} onChange={(e) => setShowLY(e.target.checked)} />
                Show LY
              </label>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-wrap gap-4">
          {/* Left column */}
          <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-2.5">
            {/* Gauge card: dial + score + "● Band" dot pill + plain-language narrative */}
            <div className="flex flex-col items-center rounded-[10px] bg-[#F9FAFB] px-3 py-3.5 text-center">
              <ClientDashboardV3ScoreGauge className="w-full max-w-[230px]" score={displayScore} />
              <div className="-mt-1.5 flex items-baseline gap-1">
                <span className="text-[30px] font-bold leading-none text-gray-900">{loading ? '—' : healthSnapshot?.score ?? '—'}</span>
                <span className="text-[13px] text-gray-500">/100</span>
              </div>
              {healthSnapshot && (
                <>
                  <div className="mt-1.5">
                    <span
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-2xl px-3 py-0.5 text-xs font-semibold"
                      style={{ background: healthSnapshot.band.pillBg, color: healthSnapshot.band.pillText }}
                    >
                      <span className="h-[7px] w-[7px] rounded-full" style={{ background: bandDotColor }} />
                      {healthSnapshot.band.label}
                    </span>
                  </div>
                  <div className="mt-2 max-w-[250px] text-[12px] leading-[1.5] text-[#475467]">
                    <strong className="text-gray-900">Your store is in {healthSnapshot.band.label.toLowerCase()} health.</strong>
                    {avgVerticalScore !== null && (
                      Math.round(healthSnapshot.score - avgVerticalScore) === 0
                        ? <> You're in line with the {healthVertical} average.</>
                        : <> You're {Math.abs(Math.round(healthSnapshot.score - avgVerticalScore))} pts {healthSnapshot.score - avgVerticalScore > 0 ? 'above' : 'below'} the {healthVertical} average.</>
                    )}
                    {avgVerticalScore !== null && isLocationFiltered && <> Peers are whole stores.</>}
                  </div>
                </>
              )}
            </div>

            {/* 2x2 stat grid: Avg in vertical | Percentile (Top X%) / vs last month | Score history */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-[10px] bg-[#F9FAFB] px-3 py-2.5">
                <div className="text-[11px] text-gray-500">Avg in {healthVertical}</div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="text-[20px] font-bold text-gray-900">{avgVerticalScore ?? '—'}</span>
                  <KpiHelpIcon tooltipKey="verticalAverage" />
                </div>
              </div>
              <div className="rounded-[10px] bg-[#F9FAFB] px-3 py-2.5">
                <div className="text-[11px] text-gray-500">Percentile{isLocationFiltered ? ' · vs whole stores' : ''}</div>
                <div className="mt-0.5 text-[20px] font-bold text-gray-900">{percentileLabel}</div>
              </div>
              <div className="rounded-[10px] bg-[#F9FAFB] px-3 py-2.5">
                <div className="text-[11px] text-gray-500">vs last month</div>
                <div className="mt-0.5 text-[20px] font-bold" style={{ color: scoreChangeColor }}>{scoreChangeLabel}</div>
              </div>
              <button
                type="button"
                onClick={() => setIsHistoryOpen(true)}
                className="flex items-center justify-center gap-1.5 rounded-[10px] bg-[#F9FAFB] px-3 py-2.5 text-[13px] font-semibold text-[#5558D4] hover:underline"
              >
                Score history <span aria-hidden="true">›</span>
              </button>
            </div>
          </div>

          {/* Right column — 5 metric rows */}
          <div className="flex min-w-0 flex-[2_1_300px] flex-col justify-between gap-3">
            {loading ? (
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">Loading KPI-derived health signals…</div>
            ) : !healthSnapshot ? (
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No KPI snapshot data is available for the current window.</div>
            ) : (
              healthSnapshot.metrics.map((metric) => (
                <div key={metric.key}>
                  <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span className="text-sm font-semibold text-gray-900">{metric.label}</span>
                    {resolveHealthRowTooltipKey(metric.label) ? (
                      <KpiHelpIcon tooltipKey={resolveHealthRowTooltipKey(metric.label) as KpiTooltipKey} />
                    ) : (
                      <Info className="h-3 w-3 text-gray-400" />
                    )}
                    <button
                      type="button"
                      onClick={() => askIndieAboutMetric(metric)}
                      className="ml-1 inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#534AB7] hover:underline"
                    >
                      <Sparkles className="h-3 w-3" />
                      Ask Indie
                    </button>
                    <span className="flex-grow" />
                    <span
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-2xl px-2.5 py-0.5 text-xs font-semibold"
                      style={{ background: TONE_LIGHT[metric.tone].bg, color: TONE_LIGHT[metric.tone].fg }}
                    >
                      <span className="h-[7px] w-[7px] rounded-full" style={{ background: TONE_LIGHT[metric.tone].dot }} />
                      {badgeForTone(metric.tone)}
                    </span>
                  </div>
                  <div className="flex w-full gap-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span
                        key={`${metric.key}-${index}`}
                        style={{
                          flex: 1,
                          height: '6px',
                          borderRadius: '3px',
                          backgroundColor: getSegmentColor(metric.tone, index, metric.filled, metric.lowerIsBetter),
                        }}
                      />
                    ))}
                  </div>
                  <div className="mt-1.5 text-[11px] text-gray-500">
                    <strong className="font-semibold text-gray-900">{metric.actualDisplay}</strong> · Bench{' '}
                    <strong className="font-semibold text-gray-900">{metric.benchmarkDisplay}</strong>
                    {showLY && (
                      <span className="text-[#98A2B3]">
                        {' '}· LY {metric.ly != null ? formatMetricValue(metric.key, metric.ly) : 'needs 3 years of history'}
                      </span>
                    )}
                    {/* Show IMU: the IMU margin sub-line under Gross Margin (not scored). */}
                    {showIMU && metric.key === 'grossMargin' && healthSnapshot.imu && (
                      <div>
                        IMU <strong className="font-semibold text-gray-900">{healthSnapshot.imu.actualDisplay}</strong> · Bench{' '}
                        <strong className="font-semibold text-gray-900">{healthSnapshot.imu.benchmarkDisplay}</strong>
                        {showLY && <span className="text-[#98A2B3]"> · LY {healthSnapshot.imu.lyDisplay}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer: outline "Break down my score" + purple "View score history" + "Updated monthly" */}
        <div className="flex flex-wrap items-center gap-3 border-t border-[#EAECF0] pt-3">
          <button
            type="button"
            onClick={() => setIsScoreDrawerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-[13px] font-semibold"
            style={{ background: '#FFFFFF', borderColor: '#D0D5DD', color: '#344054' }}
          >
            <BarChart3 className="h-[15px] w-[15px]" />
            Break down my score
          </button>
          <span className="flex-grow" />
          <button
            type="button"
            onClick={() => setIsHistoryOpen(true)}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-[#5558D4] hover:underline"
          >
            <RotateCcw className="h-3 w-3" />
            View score history
          </button>
          <span className="whitespace-nowrap text-[11px] text-gray-500">Updated monthly</span>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-5 rounded-2xl border border-gray-200 bg-white px-5 py-[24px] shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900">Cash Margin<sup className="text-[9px]">™</sup></h3>
              <KpiHelpIcon tooltipKey="cashMargin" />
            </div>
            <p className="text-xs text-gray-500">How your sales dollar flows</p>
          </div>
          {!loading && (
            <button
              type="button"
              onClick={() => setIsEditingCashCard((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <Pencil className="h-3.5 w-3.5 text-slate-600" />
              {isEditingCashCard ? 'Save' : 'Edit'}
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-5">
          <div className="mx-auto flex shrink-0 flex-col items-center gap-2">
            {loading ? (
              <div className="h-[156px] w-[156px] rounded-full border-[18px] border-gray-200" />
            ) : (
              <div
                className="relative h-[172px] w-[172px] cursor-pointer"
                onMouseLeave={() => setHoveredDonutSegment(null)}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const cx = rect.left + rect.width / 2
                  const cy = rect.top + rect.height / 2
                  const dx = e.clientX - cx
                  const dy = e.clientY - cy
                  let deg = (Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360
                  const pDeg = (purchasesPct / 100) * 360
                  const eDeg = pDeg + (expensesPct / 100) * 360

                  if (deg <= pDeg) {
                    setHoveredDonutSegment('purchases')
                  } else if (deg <= eDeg) {
                    setHoveredDonutSegment('expenses')
                  } else {
                    setHoveredDonutSegment('cashFlow')
                  }
                }}
              >
                {activeDonutTooltip && (
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-30 pointer-events-none animate-in fade-in zoom-in-95 duration-150">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs flex flex-col gap-0.5 whitespace-nowrap">
                      <span className="font-semibold text-slate-700">{activeDonutTooltip.title}</span>
                      <div className="flex items-center gap-1.5 font-bold text-slate-900">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: activeDonutTooltip.color }} />
                        <span>{activeDonutTooltip.val}</span>
                        <span className="font-normal text-slate-500">({activeDonutTooltip.pct.toFixed(0)}%)</span>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="h-full w-full rounded-full transition-all"
                  style={{ background: `conic-gradient(${donutStops})` }}
                />
                <div className="absolute inset-[22px] flex flex-col items-center justify-center rounded-full bg-white text-center">
                  <span className="text-lg font-semibold text-gray-900">{PerformanceDashboardService.moneyFormat(cashMarginDollars, true)}</span>
                  <span className="text-[11px] text-gray-500">({ReportService.formatPercent(cashMarginPct)})</span>
                  <span className="text-[11px] tracking-wide text-gray-400">Cash Margin</span>
                </div>
              </div>
            )}
            <p className="text-center text-xs text-gray-500 mt-1">Benchmark <span className="font-semibold text-gray-900">{benchmarkCashMarginPct.toFixed(1)}%</span></p>
            <button
              type="button"
              onClick={() => setIsVideoModalOpen(true)}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3.5 py-1 text-xs font-medium text-gray-700 shadow-2xs hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <PlayCircle className="h-4 w-4 text-purple-600" />
              Watch Video
            </button>
          </div>

          <div className="flex min-w-0 flex-[1_1_260px] flex-col rounded-xl bg-gray-50 p-3.5">
            {loading ? (
              <div className="rounded-xl bg-white p-4 text-sm text-gray-500">Loading cash-flow metrics…</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-gray-900" />
                    <span className="text-[13px] font-medium text-gray-800">Revenue</span>
                    <KpiHelpIcon tooltipKey="cashRevenue" />
                  </div>
                  {isEditingCashCard ? (
                    <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1">
                      <span className="text-[11px] text-gray-500">$</span>
                      <input
                        type="number"
                        min={0}
                        step={1000}
                        value={revenueInput}
                        onChange={(event) => setRevenueInput(event.target.value)}
                        className="w-24 border-0 bg-transparent text-right text-[13px] font-semibold text-gray-900 outline-none"
                      />
                    </div>
                  ) : (
                    <span className="text-[13px] font-semibold text-gray-900">{PerformanceDashboardService.moneyFormat(revenueDollars, true)}</span>
                  )}
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-purple-500" />
                      <span className="text-[13px] text-gray-800">Purchases</span>
                      <KpiHelpIcon tooltipKey="cashPurchases" />
                    </div>
                    <span className="text-[11px] text-gray-500">Benchmark {benchmarkPurchasesPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(purchasesDollars, true)}</span>
                      <DeltaBadge delta={purchasesDeltaVsBenchmark} invert />
                    </div>
                    {isEditingCashCard ? (
                      <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={purchasesPctInput}
                          onChange={(event) => setPurchasesPctInput(event.target.value)}
                          className="w-12 border-0 bg-transparent text-right text-[11px] text-gray-700 outline-none"
                        />
                        <span className="text-[11px] text-gray-500">%</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-500">({ReportService.formatPercent(purchasesPct)})</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-gray-900" />
                      <span className="text-[13px] text-gray-800">Cash Margin</span>
                      <KpiHelpIcon tooltipKey="cashMarginRow" />
                    </div>
                    <span className="text-[11px] text-gray-500">Benchmark {benchmarkCashMarginPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(cashMarginDollars, true)}</span>
                      <DeltaBadge delta={cashMarginDeltaVsBenchmark} />
                    </div>
                    <span className="text-[11px] text-gray-500">({ReportService.formatPercent(cashMarginPct)})</span>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                      <span className="text-[13px] text-gray-800">Expenses</span>
                      <KpiHelpIcon tooltipKey="cashExpenses" />
                    </div>
                    <span className="text-[11px] text-gray-500">Est. benchmark {benchmarkExpensesPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(expensesDollars, true)}</span>
                    {isEditingCashCard ? (
                      <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={expensesPctInput}
                          onChange={(event) => setExpensesPctInput(event.target.value)}
                          className="w-12 border-0 bg-transparent text-right text-[11px] text-gray-700 outline-none"
                        />
                        <span className="text-[11px] text-gray-500">%</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-500">({expensesPct.toFixed(1)}%)</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                    <span className="text-[13px] font-medium text-gray-800">Net Cash Flow</span>
                    <KpiHelpIcon tooltipKey="netCashFlow" />
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[13px] font-semibold text-emerald-600">{PerformanceDashboardService.moneyFormat(netCashFlowDollars, true)}</span>
                    <span className="text-[11px] text-gray-500">({netCashFlowPct.toFixed(1)}%)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
          {loading ? (
            <div className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">Loading ROI metrics…</div>
          ) : roiCards.map((card) => {
            const diff = card.value - card.benchmark
            return (
              <div key={card.label} className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-xs font-semibold tracking-wide text-gray-500">{card.label}</span>
                  <HelpDot />
                  <button
                    type="button"
                    onClick={() => askIndieAboutRoi(card)}
                    className="ml-1 inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#534AB7] hover:underline"
                  >
                    <Sparkles className="h-3 w-3" />
                    Ask Indie
                  </button>
                </div>
                <span className="text-[28px] font-extrabold leading-none text-gray-900">
                  {card.value > 0 ? `${card.value.toFixed(1)}×` : '—'}
                </span>
                {card.benchmark > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-0.5">
                    <span>Benchmark {card.benchmark.toFixed(1)}</span>
                    {card.value > 0 && (
                      <span className={cn('font-medium text-[10px] inline-flex items-center gap-0.5', diff >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {diff >= 0 ? '↗ +' : '↘ '}
                        {diff.toFixed(1)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {!loading && roiCards.length === 0 && (
            <div className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No ROI metrics are available for the current KPI window.</div>
          )}
        </div>
      </div>

      <HealthScoreDrawer
        isOpen={isScoreDrawerOpen && !!healthSnapshot}
        onClose={() => setIsScoreDrawerOpen(false)}
        score={displayScore}
        scoreChange={scoreChange}
        avgVerticalScore={avgVerticalScore}
        verticalRetailerCount={verticalRetailerCount}
        verticalPercentile={normalizedPercentile}
        vertical={healthVertical}
        benchmarkBasis={benchmarkBasis}
        clientLabel={clientLabel}
        throughLabel={throughLabel}
        metrics={healthSnapshot?.metrics ?? []}
        imu={healthSnapshot?.imu ?? null}
      />

      <ScoreHistorySection
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        currentScore={displayScore}
        benchmarkBasis={benchmarkBasis}
        clientLabel={clientLabel}
        throughLabel={throughLabel}
        series={historySeries}
        status={historyStatus}
      />

      <VideoPlayerModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />
    </section>
  )
}

